import React from 'react';
import { FolderKanban, Table2, HardDrive, Crown, ArrowRight } from 'lucide-react';
import { UserOrganizationSummary, OrganizationQuotaDetails } from '../../../types/domain';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { formatFileSize } from '../../../utils/fileUrl';

interface OrganizationCardProps {
  org: UserOrganizationSummary;
  quota?: OrganizationQuotaDetails;
  canManage: boolean;
  onSelect: () => void;
  onOpenUpgrade: () => void;
}

export const OrganizationCard: React.FC<OrganizationCardProps> = ({
  org,
  quota,
  canManage,
  onSelect,
  onOpenUpgrade,
}) => {
  const tablesCount = quota?.tables?.used ?? 0;

  return (
    <Card
      onClick={onSelect}
      className="p-5 bg-cf-surface border-cf-border hover:border-cf-orange/50 transition-all flex flex-col justify-between space-y-4 shadow-xs group cursor-pointer"
    >
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span className="text-base font-bold text-cf-textPrimary group-hover:text-cf-orange transition-colors truncate block">
              {org.name}
            </span>
            <span className="text-xs font-sans text-cf-textMuted">{org.slug}</span>
          </div>

          <Badge variant="default" size="xs">
            {org.plan_tier || 'Free'}
          </Badge>
        </div>

        {/* Info Row */}
        <div className="flex items-center gap-3 text-xs text-cf-textSecondary pt-1">
          <span className="flex items-center gap-1">
            <FolderKanban size={13} className="text-cf-textMuted" />
            <span>
              {org.project_count || 0} {org.project_count === 1 ? 'project' : 'projects'}
            </span>
          </span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Table2 size={13} className="text-cf-textMuted" />
            <span>
              {tablesCount} {tablesCount === 1 ? 'datastore' : 'datastores'}
            </span>
          </span>
          <span>•</span>
          <span className="capitalize font-medium text-cf-textPrimary">
            {org.role || 'Member'}
          </span>
        </div>

        {/* Storage Quota Row */}
        {quota?.storage && (
          <div className="pt-2 border-t border-cf-border/60 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-cf-textSecondary">
                <HardDrive size={12} className="text-cf-textMuted" />
                <span>Storage Used</span>
              </span>
              <span className="text-cf-textPrimary font-semibold tabular-nums text-xs">
                {formatFileSize(quota.storage.used_bytes || 0)}
                <span className="text-cf-textMuted font-normal text-xs ml-1">
                  / {formatFileSize(quota.storage.limit_bytes || 104857600)}
                </span>
              </span>
            </div>
            <div className="w-full bg-cf-bg h-1.5 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  quota.storage.exceeded
                    ? 'bg-rose-500'
                    : (quota.storage.used_bytes || 0) / (quota.storage.limit_bytes || 104857600) >
                      0.8
                    ? 'bg-amber-500'
                    : 'bg-emerald-500'
                }`}
                style={{
                  width: `${Math.min(
                    100,
                    Math.max(
                      2,
                      (((quota.storage.used_bytes || 0) /
                        (quota.storage.limit_bytes || 104857600)) *
                        100)
                    )
                  )}%`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Action Row */}
      <div className="flex items-center justify-between gap-2 pt-1">
        {canManage ? (
          <Button
            variant="outline"
            size="xs"
            leftIcon={<Crown size={13} className="text-amber-400" />}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onOpenUpgrade();
            }}
          >
            Change Plan
          </Button>
        ) : (
          <span className="text-xs text-cf-textMuted">Member Access</span>
        )}

        <div className="flex items-center gap-1 text-xs font-semibold text-cf-textPrimary group-hover:text-cf-orange transition-colors">
          <span>Open Workspace</span>
          <ArrowRight size={13} className="group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
    </Card>
  );
};
